/**
 * Fee-kept-on-refund note for a REFUND line.
 *
 * WHY this is not a finding: Shopify does not return the original payment-processing fee when an
 * order is refunded — the arithmetic is consistent, no engine rule fires, there is nothing to
 * acknowledge or resolve. It must never borrow the discrepancy/severity visual language (no chip,
 * no badge, no colour beyond what <Amount> already gives a signed number) — just a plain, muted
 * fact next to the line it happened on.
 *
 * FULL vs PARTIAL: a full refund tells us exactly what was kept (retained_fee_minor). A partial
 * refund also keeps a fee, but the seller's exact share cannot be computed until the platform's
 * fee schedule is known (engine rule R8, not written yet) — so it says a fee was withheld without
 * ever showing a number, and never 0, which would imply nothing was lost.
 */
import type { ReactNode } from "react";
import type { Line } from "../api";
import { Amount } from "./Amount";

export function RetainedFee({ line }: { line: Line }): ReactNode {
  if (line.refund_scope === "FULL") {
    return (
      <div
        className="hint"
        title="This order was fully refunded. Shopify does not return the original payment-processing fee on a refund."
      >
        fee kept on refund <Amount minor={line.retained_fee_minor} currency={line.currency} signed />
      </div>
    );
  }
  if (line.refund_scope === "PARTIAL") {
    return (
      <div
        className="hint"
        title="This order was partially refunded. The platform keeps a share of the original fee, but the exact amount can't be computed until the fee schedule is known."
      >
        fee not returned
      </div>
    );
  }
  return null;
}
